import { useState, useEffect, useMemo } from 'react';
import { expensesAPI, expenseCategoriesAPI, vendorsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Wallet, Plus, Search, Edit2, Trash2, X, IndianRupee,
  TrendingDown, TrendingUp, Filter, Calendar, BarChart3,
} from 'lucide-react';
import { cn } from '../lib/utils';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

const emptyForm = {
  date: new Date().toISOString().split('T')[0],
  category_id: '',
  vendor_id: '',
  description: '',
  amount: '',
  gst_amount: '0',
  payment_mode: 'cash',
  payment_reference: '',
  payment_status: 'paid',
  is_recurring: false,
  recurring_frequency: '',
  notes: '',
};

export const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const load = async () => {
    try {
      const [expRes, catRes, vendRes, dashRes] = await Promise.all([
        expensesAPI.list({ month: filterMonth, year: filterYear }),
        expenseCategoriesAPI.list(),
        vendorsAPI.list(),
        expensesAPI.dashboard({ month: filterMonth, year: filterYear }),
      ]);
      setExpenses(expRes.data || []);
      setCategories(catRes.data || []);
      setVendors(vendRes.data || []);
      setDashboard(dashRes.data || null);
    } catch { toast.error('Failed to load expenses'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filterMonth, filterYear]);

  const filtered = expenses.filter(e =>
    `${e.description} ${e.category_name} ${e.vendor_name} ${e.expense_number}`.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setForm({ ...emptyForm, category_id: categories[0]?.id || '' });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (exp) => {
    setForm({
      date: exp.date,
      category_id: exp.category_id,
      vendor_id: exp.vendor_id || '',
      description: exp.description,
      amount: String(exp.amount),
      gst_amount: String(exp.gst_amount),
      payment_mode: exp.payment_mode,
      payment_reference: exp.payment_reference,
      payment_status: exp.payment_status,
      is_recurring: exp.is_recurring,
      recurring_frequency: exp.recurring_frequency,
      notes: exp.notes,
    });
    setEditId(exp.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.category_id) return toast.error('Category is required');
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Valid amount is required');
    setSaving(true);
    const payload = {
      ...form,
      amount: Number(form.amount),
      gst_amount: Number(form.gst_amount || 0),
    };
    try {
      if (editId) {
        await expensesAPI.update(editId, payload);
        toast.success('Expense updated');
      } else {
        await expensesAPI.create(payload);
        toast.success('Expense recorded');
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expensesAPI.delete(id);
      toast.success('Expense deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading expenses...</div>;

  return (
    <div data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">{months[filterMonth - 1]} {filterYear} &middot; {expenses.length} record{expenses.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(Number(e.target.value))}
            className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
            data-testid="expense-month-filter"
          >
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(Number(e.target.value))}
            className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
          >
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-expense-btn">
            <Plus size={16} /> Record Expense
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium uppercase">Total Expenses</p>
            <p className="text-xl font-bold text-slate-900 mt-1"><IndianRupee size={16} className="inline" />{dashboard.total_expense?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-1">
              <TrendingDown size={14} className="text-red-500" />
              <p className="text-xs text-slate-400 font-medium uppercase">Direct Cost</p>
            </div>
            <p className="text-xl font-bold text-red-600 mt-1"><IndianRupee size={16} className="inline" />{dashboard.direct_cost?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium uppercase">Indirect Cost</p>
            <p className="text-xl font-bold text-amber-600 mt-1"><IndianRupee size={16} className="inline" />{dashboard.indirect_expense?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium uppercase">Pending</p>
            <p className="text-xl font-bold text-orange-600 mt-1"><IndianRupee size={16} className="inline" />{dashboard.total_pending?.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {dashboard?.category_breakdown?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart3 size={14} /> Expense by Category</h3>
          <div className="space-y-2">
            {dashboard.category_breakdown.map((cat, i) => {
              const pct = dashboard.total_expense > 0 ? (cat.amount / dashboard.total_expense * 100) : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-40 truncate">{cat.name}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 w-24 text-right"><IndianRupee size={10} className="inline" />{cat.amount.toLocaleString('en-IN')}</span>
                  <span className="text-[10px] text-slate-400 w-10 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search & Table */}
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search expenses..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="expense-search" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Date</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Expense #</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Category</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Vendor</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Description</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-700">Amount</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-700">Status</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-slate-400">No expenses found</td></tr>
            ) : (
              filtered.map((exp) => (
                <tr key={exp.id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`expense-row-${exp.id}`}>
                  <td className="px-4 py-3 text-slate-700">{exp.date}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{exp.expense_number}</td>
                  <td className="px-4 py-3">
                    <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                      exp.category_type === 'direct' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    )}>{exp.category_name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{exp.vendor_name || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{exp.description || '-'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    <IndianRupee size={12} className="inline" />{exp.total_amount?.toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                      exp.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    )}>{exp.payment_status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(exp)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid={`edit-expense-${exp.id}`}><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(exp.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-expense-${exp.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="expense-form-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-slate-900">{editId ? 'Edit Expense' : 'Record Expense'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="expense-date-input" />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    value={form.category_id}
                    onChange={(e) => setForm({...form, category_id: e.target.value})}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    data-testid="expense-category-select"
                  >
                    <option value="">Select category</option>
                    {categories.filter(c => c.is_active !== false).map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label>Vendor (optional)</Label>
                <select
                  value={form.vendor_id}
                  onChange={(e) => setForm({...form, vendor_id: e.target.value})}
                  className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                  data-testid="expense-vendor-select"
                >
                  <option value="">No vendor</option>
                  {vendors.filter(v => v.status === 'active').map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="What was this expense for?" data-testid="expense-desc-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="0" data-testid="expense-amount-input" />
                </div>
                <div>
                  <Label>GST Amount</Label>
                  <Input type="number" value={form.gst_amount} onChange={(e) => setForm({...form, gst_amount: e.target.value})} placeholder="0" />
                </div>
                <div>
                  <Label>Total</Label>
                  <div className="h-10 flex items-center px-3 bg-slate-50 rounded-md border border-slate-200 text-sm font-semibold">
                    <IndianRupee size={12} className="mr-1" />{(Number(form.amount || 0) + Number(form.gst_amount || 0)).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Mode</Label>
                  <select
                    value={form.payment_mode}
                    onChange={(e) => setForm({...form, payment_mode: e.target.value})}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                  >
                    {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Payment Status</Label>
                  <select
                    value={form.payment_status}
                    onChange={(e) => setForm({...form, payment_status: e.target.value})}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    data-testid="expense-status-select"
                  >
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Payment Reference</Label>
                <Input value={form.payment_reference} onChange={(e) => setForm({...form, payment_reference: e.target.value})} placeholder="Txn ID / Cheque No" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Any additional notes" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-expense-btn">
                {saving ? 'Saving...' : editId ? 'Update' : 'Record'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
